import {useEffect, useState} from 'react'
import {Link, Navigate, useSearchParams} from 'react-router-dom'
import {CircleCheck, CircleX, LoaderCircle} from 'lucide-react'
import {useAuthStore} from '@/features/auth'
import {AppHeader} from '@/widgets/app-header'
import {Button} from '@/shared/ui'
import {cn} from '@/shared/lib'

const cardClassName = cn(
  'rounded-[20px] border border-primary/15 bg-card/80 p-6 sm:p-8',
  'backdrop-blur-[20px] transition-all duration-300',
  'shadow-(--shadow-card)',
)

export function VerifiedPage() {
  const [searchParams] = useSearchParams()
  const verifyToken = searchParams.get('verify_token')
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  const createSessionFromToken = useAuthStore(s => s.createSessionFromToken)
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!verifyToken) {
      setStatus('error')
      setErrorMessage('Отсутствует токен подтверждения.')
      return
    }
    createSessionFromToken(verifyToken)
      .then(() => setStatus('success'))
      .catch((e: unknown) => {
        setStatus('error')
        setErrorMessage(e instanceof Error ? e.message : 'Не удалось создать сессию.')
      })
  }, [verifyToken, createSessionFromToken])

  if (!verifyToken && isAuthenticated) return <Navigate to="/" replace />

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden">
      <AppHeader />
      <div className="relative flex flex-1 items-center justify-center px-4 py-8 sm:px-8 sm:py-0">
        <div className="w-full max-w-[360px] animate-in fade-in slide-in-from-bottom-3 duration-500">
          <div className={cn(cardClassName, 'flex flex-col items-center gap-4 text-center')}>
            {status === 'loading' && (
              <>
                <LoaderCircle className="size-10 animate-spin text-primary" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground">Подтверждение email...</p>
              </>
            )}
            {status === 'success' && (
              <>
                <CircleCheck className="size-10 text-primary" strokeWidth={1.5} />
                <div>
                  <h1 className="text-xl font-semibold">Email подтверждён</h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Аккаунт активирован. Теперь вы можете перейти в приложение.
                  </p>
                </div>
                <Button asChild size="lg" className="w-full">
                  <Link to="/">Перейти в приложение</Link>
                </Button>
              </>
            )}
            {status === 'error' && (
              <>
                <CircleX className="size-10 text-destructive" strokeWidth={1.5} />
                <div>
                  <h1 className="text-xl font-semibold">Ошибка подтверждения</h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {errorMessage || 'Не удалось подтвердить email.'}
                  </p>
                </div>
                <Button asChild size="lg" className="w-full">
                  <Link to="/login">Войти</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
